<script lang="ts" setup>
  import ChangePasswordSettings from '@/components/account/ChangePasswordSettings.vue'
  import ChangeLanguage from '@/components/ChangeLangugeComponent.vue'
  import PermissionComponent from '@/components/PermissionRole/PermissionComponent.vue'
  import { i18n } from '@/i18n'
  import api from '@/services/api'
  import { logDeviceInfo } from '@/services/pushNotification'
  import { useBreakpointsStore, useUserStore } from '@/store'
  import { connectToZoho } from '@/utils/connectToZoho'
  import { formatPhoneNumber } from '@/utils/formatPhoneNumber'
  import * as Sentry from '@sentry/vue'
  import { captureException } from '@sentry/vue'
  import useVuelidate from '@vuelidate/core'
  import { required } from '@vuelidate/validators'
  import { storeToRefs } from 'pinia'
  import Button from 'primevue/button'
  import Divider from 'primevue/divider'
  import Textarea from 'primevue/textarea'
  import { useToast } from 'primevue/usetoast'
  import { onMounted, reactive, ref } from 'vue'
  import PermissionsInfo from './PermissionsInfo.vue'
  import { getNewIQueuePhotos } from '@/db/tables'
  import LoginUserByAdmin from '@/components/settings/admin/ChangeLoginUser/LoginUserByAdmin.vue'
  import { Capacitor } from '@capacitor/core'
  import ChangeTenantComponent from '@/components/settings/ChangeTenantComponent.vue'
  const { t } = i18n.global
  const breakpointStore = useBreakpointsStore()
  const { md } = storeToRefs(breakpointStore)
  const toast = useToast()
  const useUser = useUserStore()
  const visible = ref(false)
  const { email, phoneNumber, fullName, marketLocations, zuid, zohoIntegration } = storeToRefs(useUser)

  const diagnosticDetails = {
    release: import.meta.env.PACKAGE_VERSION,
    environment: import.meta.env.MODE,
    url: import.meta.env.VITE_BASE_URL,
    date: new Date().toString(),
  }

  const items = [
    {
      name: 'settingsIndex.changePassword',
      value: 'change-password',
      icon: 'pi pi-lock',
    },
  ]
  const zohoIsConnecting = ref(false)
  const showChangePasswordDialog = ref(false)
  const version = import.meta.env.PACKAGE_VERSION
  const userStore = useUserStore()
  const { orgDetails } = storeToRefs(userStore)
  interface IField {
    value?: string
    labelText: string
    isDisabled: boolean
  }
  interface IDiagnosticOptions {
    message: IField
  }

  const changeDialogSettings = () => {
    showChangePasswordDialog.value = !showChangePasswordDialog.value
  }

  async function initiateZohoConnection() {
    try {
      zohoIsConnecting.value = true
      await connectToZoho()
      zohoIsConnecting.value = false
    } catch (err) {
      captureException(err)
      console.log('err', err)
      zohoIsConnecting.value = false
    }
  }

  const submit = () => {
    changeDialogSettings()
    toast.add({
      severity: 'success',
      summary: t('settingsIndex.successTitle'),
      detail: t('settingsIndex.successMessage'),
      life: 3000,
    })
  }

  const errorSubmit = (message: string) => {
    toast.add({
      severity: 'error',
      summary: t('settingsIndex.errorTitle'),
      detail: message,
      life: 3000,
    })
  }
  const rules = {
    message: {
      value: { required },
    },
  }

  const form: IDiagnosticOptions = reactive({
    message: {
      labelText: 'Message',
      isDisabled: false,
    },
  })
  const validation = useVuelidate(rules, form)
  const loadingDiagnostic = ref(false)
  const handleSubmitDiagnostic = async (event: Event) => {
    event.preventDefault()

    const device = await logDeviceInfo()

    try {
      await validation.value.$validate()
      loadingDiagnostic.value = true
      const photoQueue = await getNewIQueuePhotos()
      const photoQueueError = photoQueue
        .filter((photo) => photo.status === 'ERROR')
        .map((photo) => ({
          id: photo.id,
          latitude: photo.latitude,
          longitude: photo.longitude,
          photoType: photo.photoType,
          photoTypeId: photo.photoTypeId,
          projectId: photo.projectId,
          ponumber: photo.poNumberProject,
          errorMessage: photo.errorMessage,
          lastUploadAttempt: photo.uploadDate,
          retries: photo.retries,
        }))
      api
        .post(`/Api/SendFeedback`, {
          QueueTable: '',
          Comment: `
      <div>
        <p><strong>Diagnostic Problems</strong></p>
        <p>${form.message.value}</p>
        <p>More details</p>
        <ul>
          <li><strong>Release:</strong> ${diagnosticDetails?.release}</li>
          <li><strong>Environment:</strong> ${diagnosticDetails?.environment}</li>
          <li><strong>URL:</strong> ${diagnosticDetails?.url}</li>
          <li><strong>Date:</strong> ${diagnosticDetails?.date}</li>
          <li><strong>Device:</strong> ${device}</li>
          <li><strong>Photos:</strong> ${photoQueueError.length > 0 ? JSON.stringify(photoQueueError) : 'queue is empty'}</li>
        </ul>
      </div>
      `,
        })
        .then(() => {
          visible.value = false
          loadingDiagnostic.value = false
          form.message.value = ''
          toast.add({
            severity: 'success',
            summary: t('settingsIndex.successTitleDiagnostic'),
            detail: t('settingsIndex.successDiagnostic'),
            life: 3000,
          })
        })
    } catch (error) {
      Sentry.captureException(error)
      console.log(error)
    }
  }
  const isNative = Capacitor.isNativePlatform()

  onMounted(async () => {
    await userStore.getUserInfo()
  })
</script>

<template>
  <!--
  General

  Language: Select your preferred language for the app.
  Theme: Choose between light and dark modes.
  Push notifications: Enable or disable push notifications for app updates and alerts.
  Account

  Name: Update your name as it appears in the app.
  Email: Update your email address associated with your account.
  Password: Change your account password.
  Privacy

  Analytics: Enable or disable anonymous usage data collection.
  Location: Allow or deny access to your device's location services.
  Personal data: Control how your personal data is shared and used by the app.
  -->

  <main>
    <header class="flex mt-3">
      <h1 class="mb-0 w-full">{{ t('settingsIndex.settings') }}</h1>
      <div class="col" :class="!md ? 'col-5' : 'col-2'">
        <ChangeLanguage />
      </div>
      <div class="col" :class="!md ? 'col-7' : 'col-2'">
        <Dropdown
          :options="
            items.map((i) => ({
              ...i,
              name: t(i.name),
            }))
          "
          option-label="name"
          class="w-full md:ml-2"
          :placeholder="t('settingsIndex.dialog_settings')"
          @change="
            () => {
              changeDialogSettings()
            }
          "
        />
      </div>
    </header>

    <div class="col col-12">
      <card>
        <template #content>
          <div class="flex flex-column md:flex-row">
            <div class="flex flex-column flex-grow-1 w-full gap-2">
              <div class="flex justify-content-between w-full">
                <div class="flex flex-row align-items-center gap-2">
                  <Avatar :image="'https://contacts.zoho.com/file?ID=' + zuid + '&fs=thumb'" size="large" shape="circle" />
                  <div style="font-size: 1.5rem; font-weight: 700; margin-bottom: 0.5rem">
                    {{ fullName }}
                  </div>
                </div>
              </div>

              <div v-if="email" class="flex flex-row align-items-center justify-content-start gap-2 py-2">
                <i class="pi pi-envelope" />
                {{ email }}
              </div>

              <div v-if="phoneNumber" class="flex flex-row align-items-center justify-content-start gap-2 py-2">
                <i class="pi pi-phone" />
                {{ formatPhoneNumber(phoneNumber) }}
              </div>

              <div v-if="marketLocations" class="flex flex-wrap gap-2">
                <div v-for="market in marketLocations" :key="market.id">
                  <Chip :label="market.name" icon="pi pi-map-marker" class="text-sm location-chip" style="white-space: nowrap" />
                </div>
              </div>
            </div>

            <Divider :layout="md ? 'vertical' : 'horizontal'" />

            <div v-if="orgDetails.companyName" class="flex flex-column flex-grow-1 w-full gap-2">
              <div style="font-size: 1.5rem; font-weight: 700; margin-bottom: 0.5rem">
                {{ t('settingsIndex.companyInformation') }}
              </div>
              <div class="flex flex-row gap-2 mb-2">
                <div>
                  <div class="font-bold">
                    {{ t('settingsIndex.companyName') }}
                  </div>
                  <div>{{ orgDetails.companyName }}</div>
                </div>
              </div>

              <div v-if="orgDetails.streetAddress && orgDetails.city && orgDetails.state && orgDetails.zipCode" class="mb-2">
                <div class="font-bold">{{ t('settingsIndex.address') }}</div>
                <div>{{ orgDetails.streetAddress }}</div>
                <div>
                  {{ orgDetails.city }}, {{ orgDetails.state }}
                  {{ orgDetails.zipCode }}
                </div>
              </div>

              <div v-if="orgDetails.website">
                <div class="font-bold">{{ t('settingsIndex.website') }}</div>
                <div>
                  <a :href="orgDetails.website">{{ orgDetails.website }}</a>
                </div>
              </div>
            </div>
          </div>
        </template>
      </card>
    </div>
    <PermissionComponent :key-name="'integrations'" :nodo-name="'settingsPermissions'">
      <div class="col col-12 mt-4">
        <h1>{{ t('settingsIndex.integrations') }}</h1>
        <div class="text-lg text-color-secondary">
          {{ t('settingsIndex.pageDescription') }}
        </div>
      </div>

      <div class="col col-12 md:col-12">
        <card>
          <template #content>
            <div v-if="md" class="flex justify-content-between px-2 py-4 align-items-center">
              <div class="flex w-5rem">
                <svg class="w-3rem" data-name="zoho crm" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 348.53 347.38">
                  <path
                    d="m167.91.1c48.56,0,97.13-.03,145.69-.1,7.07-.01,13.55,1.73,19.29,5.86,6.83,4.91,12.35,10.98,14.59,19.26,1.41,5.25.99,10.78.99,16.18.04,71.43.03,142.86.04,214.29,0,14.67.04,29.34-.02,44-.03,6.21.09,12.46-.49,18.63-1.02,10.78-6.6,18.89-15.74,24.68-4.64,2.94-9.58,4.42-15.1,4.41-47.74-.04-95.49-.02-143.23-.02h0c-46.94,0-93.88-.1-140.82.1-10.1.04-17.86-4.04-24.35-11.17-5.44-5.98-8.43-12.92-8.53-21.24-.45-37.83-.11-75.66-.15-113.49-.06-55.66,0-111.31-.07-166.97,0-6.15,1.41-11.57,4.46-17.08C9.83,7.74,18.01,2.74,28.34.41c1.45-.33,3-.34,4.5-.34m12.45,171.81c0,3.7.07,6.31-.01,8.92-.23,7.48,1.68,14.73,4.52,21.32,3.07,7.15,7.3,13.9,11.77,20.46,9.04,13.26,21.79,21.71,36.05,27.59,16.89,6.97,34.53,7.38,52.18,1.84,2.84-.89,4.2-2.29,4.65-5.05.49-3.01.53-5.86-2.17-7.95-2.35-1.81-4.77-2.26-7.87-1.52-22.08,5.32-42.51,2.2-60.26-12.78-14.35-12.11-22.18-27.67-23.75-46.34-.81-9.68.37-19.16,3.84-28.19,3.43-8.93,8.74-16.7,15.72-23.4,16.95-16.27,37.4-21.13,59.89-17.44,10.59,1.74,20.36,6.68,28.93,13.52,2.27,1.81,4.1,4,6.23,5.91,10.99,9.84,20.32,21.35,31.35,31.17,4.24,3.77,7.88,8.21,11.91,12.22,3.69,3.68,4.41,11.25.97,15.15-4.92,5.58-11.43,8.12-18.31,1.99-.82-.74-1.67-1.45-2.49-2.19-9.24-8.31-17.16-17.94-26.39-26.28-4.97-4.49-9.51-9.44-14.38-14.05-10.03-9.48-28.32-8.55-37.92,1.86-9.17,9.94-10.42,27.69,1.61,39.41,14.45,14.06,28.73,28.3,42.98,42.57,11.41,11.43,24.96,19.55,41.27,23.03,17.24,3.68,33.67,1.85,49.47-5.19,14.26-6.36,25.84-16.02,34.9-29.07,8.9-12.8,13.02-26.86,14.52-41.99.55-5.53-.67-10.93-1.54-16.38-1.13-7.03-2.86-13.85-5.91-20.22-6.85-14.32-16.67-26.03-30.25-34.71-17.06-10.9-35.32-15.58-55.42-12.58-4.46.67-9.02,1.04-13.15,3.11-2.47,1.23-4.11,2.97-4.43,5.96-.53,5.05,5.1,9.91,10.56,7.98,10-3.54,20.66-3.54,30.45-1.48,31.57,6.64,46.93,27.19,52.81,52.6,1.68,7.25,1.47,14.92.3,22.21-3.05,19.09-12.5,34.59-28.79,45.33-11.28,7.43-23.86,10.97-37.54,10.63-17.83-.44-33.18-6.4-45.73-19.23-7.79-7.96-15.56-15.95-23.62-23.63-7.84-7.49-15.55-15.09-22.72-23.21-3.17-3.59-2.48-7.6-.55-11.41,1.8-3.55,4.54-5.84,8.87-6.29,6.33-.67,10.94,2.25,14.86,6.5,11.93,12.94,25.04,24.7,37.33,37.28,7.97,8.15,17.96,9.4,28.7,6,10.11-3.2,15.14-10.92,17.38-20.43,2.14-9.09-1.02-17.46-7.12-24.14-10.67-11.69-21.94-22.84-33.52-33.64-6.37-5.94-11.92-12.66-19.23-17.72-10.44-7.21-21.05-13.55-33.81-15.68-8.87-1.48-17.64-2.3-26.68-.89-23.63,3.68-42.44,15.13-56.16,34.48-9.49,13.39-15.54,28.17-14.3,44Z"
                    style="fill: #4084be"
                  />
                  <path
                    d="m44.67,171.33c-1.24-15.84,4.81-30.61,14.3-44,13.71-19.35,32.52-30.8,56.16-34.48,9.04-1.41,17.81-.59,26.68.89,12.75,2.13,23.37,8.46,33.81,15.68,7.32,5.06,12.87,11.78,19.23,17.72,11.58,10.8,22.85,21.95,33.52,33.64,6.1,6.68,9.26,15.05,7.12,24.14-2.24,9.5-7.28,17.23-17.38,20.43-10.74,3.4-20.74,2.15-28.7-6-12.29-12.58-25.4-24.34-37.33-37.28-3.92-4.25-8.54-7.17-14.86-6.5-4.33.46-7.07,2.74-8.87,6.29-1.93,3.81-2.62,7.82.55,11.41,7.17,8.12,14.88,15.73,22.72,23.21,8.05,7.69,15.83,15.67,23.62,23.63,12.56,12.83,27.9,18.79,45.73,19.23,13.68.34,26.26-3.2,37.54-10.63,16.29-10.74,25.74-26.24,28.79-45.33,1.17-7.29,1.38-14.95-.3-22.21-5.88-25.41-21.24-45.96-52.81-52.6-9.79-2.06-20.46-2.06-30.45,1.48-5.46,1.93-11.09-2.93-10.56-7.98.32-2.99,1.96-4.73,4.43-5.96,4.13-2.07,8.69-2.44,13.15-3.11,20.1-3,38.37,1.67,55.42,12.58,13.58,8.68,23.4,20.39,30.25,34.71,3.05,6.37,4.78,13.19,5.91,20.22.87,5.46,2.09,10.85,1.54,16.38-1.5,15.13-5.62,29.19-14.52,41.99-9.06,13.04-20.64,22.71-34.9,29.07-15.8,7.04-32.23,8.86-49.47,5.19-16.31-3.48-29.86-11.6-41.27-23.03-14.25-14.27-28.53-28.51-42.98-42.57-12.04-11.72-10.79-29.47-1.61-39.41,9.6-10.4,27.9-11.34,37.92-1.86,4.87,4.6,9.41,9.56,14.38,14.05,9.23,8.34,17.15,17.96,26.39,26.28.82.74,1.67,1.45,2.49,2.19,6.88,6.14,13.38,3.6,18.31-1.99,3.44-3.9,2.72-11.47-.97-15.15-4.03-4.02-7.67-8.45-11.91-12.22-11.03-9.82-20.36-21.32-31.35-31.17-2.13-1.91-3.96-4.1-6.23-5.91-8.58-6.84-18.34-11.78-28.93-13.52-22.48-3.7-42.94,1.17-59.89,17.44-6.98,6.7-12.29,14.46-15.72,23.4-3.47,9.04-4.65,18.52-3.84,28.19,1.57,18.68,9.4,34.23,23.75,46.34,17.75,14.98,38.18,18.09,60.26,12.78,3.1-.75,5.52-.3,7.87,1.52,2.7,2.09,2.66,4.94,2.17,7.95-.45,2.76-1.81,4.16-4.65,5.05-17.65,5.54-35.28,5.13-52.18-1.84-14.26-5.88-27.01-14.34-36.05-27.59-4.47-6.56-8.7-13.31-11.77-20.46-2.83-6.59-4.75-13.84-4.52-21.32.08-2.61.01-5.22.01-8.92Z"
                    style="fill: #fff"
                  />
                </svg>
              </div>
              <div class="flex flex-grow-1">
                <div>
                  <div class="font-semibold text-xl mb-2">Zoho CRM</div>
                  <div>
                    {{ t('settingsIndex.connectWithZohoDescription') }}
                  </div>
                </div>
              </div>
              <div class="flex">
                <Button :loading="zohoIsConnecting" class="mb2" :disabled="zohoIntegration" @click="initiateZohoConnection">
                  <span v-if="zohoIntegration">{{ t('settingsIndex.connected') }}</span>
                  <span v-else>{{ t('settingsIndex.connect') }}</span>
                </Button>
              </div>
            </div>
            <div v-else class="flex flex-column justify-content-between px-2 py-2 align-items-start">
              <div class="flex align-items-center gap-3">
                <div class="flex w-3rem">
                  <svg class="w-3rem" data-name="zoho crm" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 348.53 347.38">
                    <path
                      d="m167.91.1c48.56,0,97.13-.03,145.69-.1,7.07-.01,13.55,1.73,19.29,5.86,6.83,4.91,12.35,10.98,14.59,19.26,1.41,5.25.99,10.78.99,16.18.04,71.43.03,142.86.04,214.29,0,14.67.04,29.34-.02,44-.03,6.21.09,12.46-.49,18.63-1.02,10.78-6.6,18.89-15.74,24.68-4.64,2.94-9.58,4.42-15.1,4.41-47.74-.04-95.49-.02-143.23-.02h0c-46.94,0-93.88-.1-140.82.1-10.1.04-17.86-4.04-24.35-11.17-5.44-5.98-8.43-12.92-8.53-21.24-.45-37.83-.11-75.66-.15-113.49-.06-55.66,0-111.31-.07-166.97,0-6.15,1.41-11.57,4.46-17.08C9.83,7.74,18.01,2.74,28.34.41c1.45-.33,3-.34,4.5-.34m12.45,171.81c0,3.7.07,6.31-.01,8.92-.23,7.48,1.68,14.73,4.52,21.32,3.07,7.15,7.3,13.9,11.77,20.46,9.04,13.26,21.79,21.71,36.05,27.59,16.89,6.97,34.53,7.38,52.18,1.84,2.84-.89,4.2-2.29,4.65-5.05.49-3.01.53-5.86-2.17-7.95-2.35-1.81-4.77-2.26-7.87-1.52-22.08,5.32-42.51,2.2-60.26-12.78-14.35-12.11-22.18-27.67-23.75-46.34-.81-9.68.37-19.16,3.84-28.19,3.43-8.93,8.74-16.7,15.72-23.4,16.95-16.27,37.4-21.13,59.89-17.44,10.59,1.74,20.36,6.68,28.93,13.52,2.27,1.81,4.1,4,6.23,5.91,10.99,9.84,20.32,21.35,31.35,31.17,4.24,3.77,7.88,8.21,11.91,12.22,3.69,3.68,4.41,11.25.97,15.15-4.92,5.58-11.43,8.12-18.31,1.99-.82-.74-1.67-1.45-2.49-2.19-9.24-8.31-17.16-17.94-26.39-26.28-4.97-4.49-9.51-9.44-14.38-14.05-10.03-9.48-28.32-8.55-37.92,1.86-9.17,9.94-10.42,27.69,1.61,39.41,14.45,14.06,28.73,28.3,42.98,42.57,11.41,11.43,24.96,19.55,41.27,23.03,17.24,3.68,33.67,1.85,49.47-5.19,14.26-6.36,25.84-16.02,34.9-29.07,8.9-12.8,13.02-26.86,14.52-41.99.55-5.53-.67-10.93-1.54-16.38-1.13-7.03-2.86-13.85-5.91-20.22-6.85-14.32-16.67-26.03-30.25-34.71-17.06-10.9-35.32-15.58-55.42-12.58-4.46.67-9.02,1.04-13.15,3.11-2.47,1.23-4.11,2.97-4.43,5.96-.53,5.05,5.1,9.91,10.56,7.98,10-3.54,20.66-3.54,30.45-1.48,31.57,6.64,46.93,27.19,52.81,52.6,1.68,7.25,1.47,14.92.3,22.21-3.05,19.09-12.5,34.59-28.79,45.33-11.28,7.43-23.86,10.97-37.54,10.63-17.83-.44-33.18-6.4-45.73-19.23-7.79-7.96-15.56-15.95-23.62-23.63-7.84-7.49-15.55-15.09-22.72-23.21-3.17-3.59-2.48-7.6-.55-11.41,1.8-3.55,4.54-5.84,8.87-6.29,6.33-.67,10.94,2.25,14.86,6.5,11.93,12.94,25.04,24.7,37.33,37.28,7.97,8.15,17.96,9.4,28.7,6,10.11-3.2,15.14-10.92,17.38-20.43,2.14-9.09-1.02-17.46-7.12-24.14-10.67-11.69-21.94-22.84-33.52-33.64-6.37-5.94-11.92-12.66-19.23-17.72-10.44-7.21-21.05-13.55-33.81-15.68-8.87-1.48-17.64-2.3-26.68-.89-23.63,3.68-42.44,15.13-56.16,34.48-9.49,13.39-15.54,28.17-14.3,44Z"
                      style="fill: #4084be"
                    />
                    <path
                      d="m44.67,171.33c-1.24-15.84,4.81-30.61,14.3-44,13.71-19.35,32.52-30.8,56.16-34.48,9.04-1.41,17.81-.59,26.68.89,12.75,2.13,23.37,8.46,33.81,15.68,7.32,5.06,12.87,11.78,19.23,17.72,11.58,10.8,22.85,21.95,33.52,33.64,6.1,6.68,9.26,15.05,7.12,24.14-2.24,9.5-7.28,17.23-17.38,20.43-10.74,3.4-20.74,2.15-28.7-6-12.29-12.58-25.4-24.34-37.33-37.28-3.92-4.25-8.54-7.17-14.86-6.5-4.33.46-7.07,2.74-8.87,6.29-1.93,3.81-2.62,7.82.55,11.41,7.17,8.12,14.88,15.73,22.72,23.21,8.05,7.69,15.83,15.67,23.62,23.63,12.56,12.83,27.9,18.79,45.73,19.23,13.68.34,26.26-3.2,37.54-10.63,16.29-10.74,25.74-26.24,28.79-45.33,1.17-7.29,1.38-14.95-.3-22.21-5.88-25.41-21.24-45.96-52.81-52.6-9.79-2.06-20.46-2.06-30.45,1.48-5.46,1.93-11.09-2.93-10.56-7.98.32-2.99,1.96-4.73,4.43-5.96,4.13-2.07,8.69-2.44,13.15-3.11,20.1-3,38.37,1.67,55.42,12.58,13.58,8.68,23.4,20.39,30.25,34.71,3.05,6.37,4.78,13.19,5.91,20.22.87,5.46,2.09,10.85,1.54,16.38-1.5,15.13-5.62,29.19-14.52,41.99-9.06,13.04-20.64,22.71-34.9,29.07-15.8,7.04-32.23,8.86-49.47,5.19-16.31-3.48-29.86-11.6-41.27-23.03-14.25-14.27-28.53-28.51-42.98-42.57-12.04-11.72-10.79-29.47-1.61-39.41,9.6-10.4,27.9-11.34,37.92-1.86,4.87,4.6,9.41,9.56,14.38,14.05,9.23,8.34,17.15,17.96,26.39,26.28.82.74,1.67,1.45,2.49,2.19,6.88,6.14,13.38,3.6,18.31-1.99,3.44-3.9,2.72-11.47-.97-15.15-4.03-4.02-7.67-8.45-11.91-12.22-11.03-9.82-20.36-21.32-31.35-31.17-2.13-1.91-3.96-4.1-6.23-5.91-8.58-6.84-18.34-11.78-28.93-13.52-22.48-3.7-42.94,1.17-59.89,17.44-6.98,6.7-12.29,14.46-15.72,23.4-3.47,9.04-4.65,18.52-3.84,28.19,1.57,18.68,9.4,34.23,23.75,46.34,17.75,14.98,38.18,18.09,60.26,12.78,3.1-.75,5.52-.3,7.87,1.52,2.7,2.09,2.66,4.94,2.17,7.95-.45,2.76-1.81,4.16-4.65,5.05-17.65,5.54-35.28,5.13-52.18-1.84-14.26-5.88-27.01-14.34-36.05-27.59-4.47-6.56-8.7-13.31-11.77-20.46-2.83-6.59-4.75-13.84-4.52-21.32.08-2.61.01-5.22.01-8.92Z"
                      style="fill: #fff"
                    />
                  </svg>
                </div>
                <div class="font-semibold text-xl mb-2">Zoho CRM</div>
              </div>
              <div class="pt-2">
                {{ t('settingsIndex.connectWithZohoDescription') }}
              </div>
              <div class="flex w-full justify-content-end pt-4">
                <Button :loading="zohoIsConnecting" class="mb2" :disabled="zohoIntegration" @click="initiateZohoConnection">
                  <span v-if="zohoIntegration">{{ t('settingsIndex.connected') }}</span>
                  <span v-else>{{ t('settingsIndex.connect') }}</span>
                </Button>
              </div>
            </div>
            <!-- <hr />
            <div
              class="flex justify-content-between px-2 py-4 align-items-center"
            >
              <div class="flex w-5rem">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-3rem"
                  viewBox="0 0 326667 333333"
                  shape-rendering="geometricPrecision"
                  text-rendering="geometricPrecision"
                  image-rendering="optimizeQuality"
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                >
                  <path
                    d="M326667 170370c0-13704-1112-23704-3518-34074H166667v61851h91851c-1851 15371-11851 38519-34074 54074l-311 2071 49476 38329 3428 342c31481-29074 49630-71852 49630-122593m0 0z"
                    fill="#4285f4"
                  />
                  <path
                    d="M166667 333333c44999 0 82776-14815 110370-40370l-52593-40742c-14074 9815-32963 16667-57777 16667-44074 0-81481-29073-94816-69258l-1954 166-51447 39815-673 1870c27407 54444 83704 91852 148890 91852z"
                    fill="#34a853"
                  />
                  <path
                    d="M71851 199630c-3518-10370-5555-21482-5555-32963 0-11482 2036-22593 5370-32963l-93-2209-52091-40455-1704 811C6482 114444 1 139814 1 166666s6482 52221 17777 74814l54074-41851m0 0z"
                    fill="#fbbc04"
                  />
                  <path
                    d="M166667 64444c31296 0 52406 13519 64444 24816l47037-45926C249260 16482 211666 1 166667 1 101481 1 45185 37408 17777 91852l53889 41853c13520-40185 50927-69260 95001-69260m0 0z"
                    fill="#ea4335"
                  />
                </svg>
              </div>
              <div class="flex flex-grow-1">
                <div>
                  <div class="font-semibold text-xl mb-2">Google Workspace</div>
                  <div>
                    {{
                      $t('settingsIndex.connectWithGoogleWorkspaceDescription')
                    }}
                  </div>
                </div>
              </div>
              <div class="flex">
                <Button
                  :loading="zohoIsConnecting"
                  class="mb2"
                  :disabled="zohoIntegration"
                  @click="initiateZohoConnection"
                >
                  <span v-if="true">{{ $t('settingsIndex.connected') }}</span>
                  <span v-else>{{ $t('settingsIndex.connect') }}</span>
                </Button>
              </div>
            </div>
            <hr />
            <div
              class="flex justify-content-between px-2 py-4 align-items-center"
            >
              <div class="flex w-5rem">
                <svg
                  class="w-3rem"
                  viewBox="0 -9.2 960 1074.5000000000002"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <radialGradient
                    id="a"
                    cx="322"
                    cy="207.3"
                    gradientUnits="userSpaceOnUse"
                    r="800.8"
                  >
                    <stop offset=".064" stop-color="#ae7fe2" />
                    <stop offset="1" stop-color="#0078d4" />
                  </radialGradient>
                  <linearGradient
                    id="b"
                    gradientUnits="userSpaceOnUse"
                    x1="324.3"
                    x2="210"
                    y1="860.8"
                    y2="663.2"
                  >
                    <stop offset="0" stop-color="#114a8b" />
                    <stop offset="1" stop-color="#0078d4" stop-opacity="0" />
                  </linearGradient>
                  <radialGradient
                    id="c"
                    cx="154.3"
                    cy="824.4"
                    gradientUnits="userSpaceOnUse"
                    r="745.2"
                  >
                    <stop offset=".134" stop-color="#d59dff" />
                    <stop offset="1" stop-color="#5e438f" />
                  </radialGradient>
                  <linearGradient
                    id="d"
                    gradientUnits="userSpaceOnUse"
                    x1="872.6"
                    x2="750.1"
                    y1="561"
                    y2="736.6"
                  >
                    <stop offset="0" stop-color="#493474" />
                    <stop offset="1" stop-color="#8c66ba" stop-opacity="0" />
                  </linearGradient>
                  <radialGradient
                    id="e"
                    cx="889.3"
                    cy="588.1"
                    gradientUnits="userSpaceOnUse"
                    r="598.1"
                  >
                    <stop offset=".058" stop-color="#50e6ff" />
                    <stop offset="1" stop-color="#436dcd" />
                  </radialGradient>
                  <linearGradient
                    id="f"
                    gradientUnits="userSpaceOnUse"
                    x1="311.4"
                    x2="491.7"
                    y1="25.4"
                    y2="25.4"
                  >
                    <stop offset="0" stop-color="#2d3f80" />
                    <stop offset="1" stop-color="#436dcd" stop-opacity="0" />
                  </linearGradient>
                  <path
                    d="M386 24.6l-5.4 3.3c-8.5 5.2-16.6 11-24.2 17.3L372 34.3h132L528 216 408 336l-120 83.4v96.2c0 67.2 35.1 129.5 92.6 164.2l126.3 76.5L240 912h-51.5l-95.9-58.1C35.1 819.1 0 756.9 0 689.7V366.3C0 299.1 35.1 236.7 92.6 202l288-174.2q2.7-1.7 5.4-3.2z"
                    fill="url(#a)"
                  />
                  <path
                    d="M386 24.6l-5.4 3.3c-8.5 5.2-16.6 11-24.2 17.3L372 34.3h132L528 216 408 336l-120 83.4v96.2c0 67.2 35.1 129.5 92.6 164.2l126.3 76.5L240 912h-51.5l-95.9-58.1C35.1 819.1 0 756.9 0 689.7V366.3C0 299.1 35.1 236.7 92.6 202l288-174.2q2.7-1.7 5.4-3.2z"
                    fill="url(#b)"
                  />
                  <path
                    d="M936 576l24 36v77.7c0 67.1-35.1 129.4-92.6 164.2l-288 174.4c-61.1 37-137.7 37-198.8 0L99.3 858c59.9 33.1 133.2 31.8 192.1-3.9l288-174.3C636.9 645 672 582.7 672 515.5V408z"
                    fill="url(#c)"
                  />
                  <path
                    d="M936 576l24 36v77.7c0 67.1-35.1 129.4-92.6 164.2l-288 174.4c-61.1 37-137.7 37-198.8 0L99.3 858c59.9 33.1 133.2 31.8 192.1-3.9l288-174.3C636.9 645 672 582.7 672 515.5V408z"
                    fill="url(#d)"
                  />
                  <path
                    d="M960 366.3v323.4q0 3.1-.1 6.3c-2.1-64.8-36.8-124.3-92.5-158l-288-174.2c-61.1-37-137.7-37-198.8 0l-92.6 56V192.2c0-67.2 35.1-129.5 92.6-164.3l5.7-3.5C446.5-9.2 520.2-8 579.4 27.8l288 174.2c57.5 34.7 92.6 97.1 92.6 164.3z"
                    fill="url(#e)"
                  />
                  <path
                    d="M960 366.3v323.4q0 3.1-.1 6.3c-2.1-64.8-36.8-124.3-92.5-158l-288-174.2c-61.1-37-137.7-37-198.8 0l-92.6 56V192.2c0-67.2 35.1-129.5 92.6-164.3l5.7-3.5C446.5-9.2 520.2-8 579.4 27.8l288 174.2c57.5 34.7 92.6 97.1 92.6 164.3z"
                    fill="url(#f)"
                  />
                </svg>
              </div>
              <div class="flex flex-grow-1">
                <div>
                  <div class="font-semibold text-xl mb-2">Office 365</div>
                  <div>
                    {{ $t('settingsIndex.connectWithOffice365Description') }}
                  </div>
                </div>
              </div>
              <div class="flex">
                <Button
                  :loading="zohoIsConnecting"
                  class="mb2"
                  :disabled="false"
                  @click="() => console.log('connecting')"
                >
                  <span v-if="false">{{ $t('settingsIndex.connected') }}</span>
                  <span v-else>{{ $t('settingsIndex.connect') }}</span>
                </Button>
              </div>
            </div> -->
          </template>
        </card>
      </div>
    </PermissionComponent>
    <PermissionsInfo />
    <Dialog v-model:visible="visible" modal :header="t('settingsIndex.settingsTitleDiagnostic')" :style="{ width: '50rem' }" :breakpoints="{ '1199px': '75vw', '575px': '90vw' }">
      <form class="flex flex-column gap-3" @submit="handleSubmitDiagnostic">
        <p class="m-0">
          {{ t('settingsIndex.settingsDescriptionDiagnostic') }}
        </p>
        <Textarea v-model="form.message.value" rows="5" cols="30" auto-resize :style="{ width: '100%' }" />
        <Button :loading="loadingDiagnostic" type="submit" :label="t('settingsIndex.settingsSendDiagnostic')" />
      </form>
    </Dialog>
    <div class="grid col-2 col-offset-2"></div>
    <div :class="isNative ? 'w-full MobileOptions py-4 gap-3' : 'w-full flex justify-content-end py-4 gap-3'">
      <ChangeTenantComponent />
      <div :class="isNative ? 'MobileFirstOption' : ''">
        <LoginUserByAdmin />
      </div>
      <Button :label="t('settingsIndex.settingsDiagnostic')" severity="danger" @click="visible = true" />
      <div class="p-card p-3">Version: {{ version }}</div>
    </div>
  </main>
  <Dialog
    v-model:visible="showChangePasswordDialog"
    :position="!md ? 'bottom' : undefined"
    class="w-full m-0"
    style="max-width: 36rem"
    :modal="true"
    :dismissable-mask="true"
    :draggable="false"
    :close-on-escape="true"
    :closable="true"
    :header="t('settingsIndex.changePassword')"
  >
    <ChangePasswordSettings @submit-success="submit" @submit-error="errorSubmit" />
  </Dialog>
  <Toast position="bottom-right" />
</template>

<style scoped>
  .MobileOptions {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
  .MobileFirstOption {
    grid-column: 1 /3;
  }
</style>
